/**
 * Phase 6: hands-on dice. Drag a die in the tray and flick it: it tumbles with simple physics while the server
 * rolls; the dice settle on the server's numbers (the throw never changes the result).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { stepDice, throwVelocity, type DiceRoll, type DieBody } from "@dm/shared";

const HAND_KEY = "dm.handDice";
export function useHandDice(): [boolean, (v: boolean) => void] {
  const [on, setOn] = useState(() => localStorage.getItem(HAND_KEY) === "1");
  return [on, (v: boolean) => {
    localStorage.setItem(HAND_KEY, v ? "1" : "0");
    setOn(v);
  }];
}

interface Die extends DieBody { sides: number; face: number; final: number | null; dropped: boolean }

const SHAPES: Record<number, number> = { 4: 3, 6: 4, 8: 4, 10: 5, 12: 5, 20: 6, 100: 10 };

function drawDie(ctx: CanvasRenderingContext2D, d: Die, nat: "20" | "1" | null) {
  const n = SHAPES[d.sides] ?? 6;
  ctx.save();
  ctx.translate(d.x, d.y);
  ctx.rotate(d.a + (d.sides === 8 ? Math.PI / 4 : 0));
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2 - Math.PI / 2;
    const r = d.sides === 6 ? d.r * 0.95 : d.r;
    ctx[i === 0 ? "moveTo" : "lineTo"](Math.cos(t) * r, Math.sin(t) * r);
  }
  ctx.closePath();
  ctx.globalAlpha = d.dropped && d.final !== null ? 0.35 : 1;
  ctx.fillStyle = nat === "20" ? "#2f6b2c" : nat === "1" ? "#6b2a22" : "#8a5a1c";
  ctx.strokeStyle = "#f0d9a8";
  ctx.lineWidth = 2;
  ctx.fill();
  ctx.stroke();
  ctx.rotate(-(d.a + (d.sides === 8 ? Math.PI / 4 : 0)) * (d.final !== null ? 1 : 0));
  ctx.fillStyle = "#fff8e8";
  ctx.font = `bold ${Math.round(d.r * (d.sides === 100 ? 0.6 : 0.8))}px Georgia, serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(d.final ?? d.face), 0, 1);
  ctx.restore();
}

/**
 * The tray. `sides`/`count`/`adv` describe what will be thrown; `onThrow` starts the server roll and resolves with
 * the result (or null on error).
 */
export function ThrowTray({ sides, count, extra, onThrow, disabled }: {
  sides: number; count: number; extra: number; disabled: boolean; onThrow: () => Promise<DiceRoll | null>;
}) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const dice = useRef<Die[]>([]);
  const drag = useRef<{ pts: Array<{ x: number; y: number; t: number }> } | null>(null);
  const raf = useRef<number | null>(null);
  const [status, setStatus] = useState("Drag a die and flick it.");
  const [rolling, setRolling] = useState(false);
  const n = Math.min(20, count + extra);

  const size = () => {
    const c = canvas.current!;
    return { w: c.clientWidth, h: c.clientHeight };
  };
  const place = useCallback(() => {
    const c = canvas.current;
    if (!c) return;
    const { w, h } = { w: c.clientWidth, h: c.clientHeight };
    const r = n > 6 ? 16 : 22;
    dice.current = Array.from({ length: n }, (_, i) => ({
      x: Math.min(w - r, 30 + (i % 10) * (r * 2 + 4)), y: h - r - 8 - Math.floor(i / 10) * (r * 2 + 4), vx: 0, vy: 0, a: 0, va: 0, r,
      sides, face: sides, final: null, dropped: false,
    }));
  }, [n, sides]);

  const draw = useCallback((nat: "20" | "1" | null = null) => {
    const c = canvas.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    if (c.width !== c.clientWidth * dpr) {
      c.width = c.clientWidth * dpr;
      c.height = c.clientHeight * dpr;
    }
    const ctx = c.getContext("2d")!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, c.clientWidth, c.clientHeight);
    for (const d of dice.current) drawDie(ctx, d, d.dropped ? null : nat);
  }, []);

  useEffect(() => {
    if (rolling) return;
    place();
    draw();
  }, [place, draw, rolling]);
  useEffect(() => () => {
    if (raf.current) cancelAnimationFrame(raf.current);
  }, []);

  const animate = (result: Promise<DiceRoll | null>) => {
    setRolling(true);
    setStatus("Rolling…");
    let roll: DiceRoll | null | undefined;
    void result.then((r) => (roll = r));
    const started = performance.now();
    let last = started;
    let lastFace = 0;
    const tick = (now: number) => {
      const dt = Math.min(0.033, (now - last) / 1000);
      last = now;
      const { w, h } = size();
      const moving = stepDice(dice.current, w, h, dt);
      if (now - lastFace > 70) {
        lastFace = now;
        for (const d of dice.current) if (Math.hypot(d.vx, d.vy) > 30) d.face = 1 + Math.floor(Math.random() * d.sides);
      }
      const done = !moving && now - started > 700 && roll !== undefined;
      if (done || now - started > 6000) {
        if (roll) {
          const kept = [...roll.dice];
          const dropped = [...roll.dropped];
          dice.current.forEach((d, i) => {
            if (i < kept.length) d.final = kept[i]!;
            else if (i - kept.length < dropped.length) {
              d.final = dropped[i - kept.length]!;
              d.dropped = true;
            } else d.final = d.face;
          });
          const nat = roll.sides === 20 && roll.count === 1 ? (roll.dice[0] === 20 ? "20" : roll.dice[0] === 1 ? "1" : null) : null;
          draw(nat);
          setStatus(`${roll.formula} = ${roll.total}${nat === "20" ? " · natural 20!" : nat === "1" ? " · natural 1" : ""}`);
        } else {
          draw();
          setStatus("That roll didn't go through. Try again.");
        }
        raf.current = null;
        setTimeout(() => setRolling(false), 2500);
        return;
      }
      draw();
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };

  const release = (vx: number, vy: number) => {
    const ds = dice.current;
    ds.forEach((d, i) => {
      const v = throwVelocity(vx * (0.9 + 0.2 * Math.random()), vy * (0.9 + 0.2 * Math.random()));
      d.vx = v.vx + (i - ds.length / 2) * 40;
      d.vy = v.vy;
      d.va = (Math.random() - 0.5) * 30;
      d.final = null;
      d.dropped = false;
    });
    animate(onThrow());
  };

  const pos = (e: React.PointerEvent) => {
    const r = canvas.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top, t: performance.now() };
  };
  const onDown = (e: React.PointerEvent) => {
    if (disabled || rolling) return;
    const p = pos(e);
    const hit = dice.current.some((d) => Math.hypot(d.x - p.x, d.y - p.y) < d.r * 1.6);
    if (!hit) return;
    canvas.current!.setPointerCapture(e.pointerId);
    drag.current = { pts: [p] };
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const p = pos(e);
    const prev = drag.current.pts.at(-1)!;
    drag.current.pts = [...drag.current.pts.filter((q) => p.t - q.t < 100), p];
    const { w, h } = size();
    for (const d of dice.current) {
      d.x = Math.min(w - d.r, Math.max(d.r, d.x + (p.x - prev.x)));
      d.y = Math.min(h - d.r, Math.max(d.r, d.y + (p.y - prev.y)));
      d.a += (p.x - prev.x) * 0.02;
    }
    draw();
  };
  const onUp = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const pts = drag.current.pts;
    drag.current = null;
    canvas.current!.releasePointerCapture(e.pointerId);
    const a = pts[0]!;
    const b = pts.at(-1)!;
    const dt = Math.max(16, b.t - a.t) / 1000;
    release((b.x - a.x) / dt, (b.y - a.y) / dt);
  };

  return (
    <div className="throw-tray">
      <canvas
        ref={canvas}
        className={rolling ? "rolling" : ""}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={() => (drag.current = null)}
        aria-label={`Dice tray: drag and flick to throw ${n}d${sides}`}
        role="img"
      />
      <div className="row">
        <span className="small" aria-live="polite">{status}</span>
        <button disabled={disabled || rolling} onClick={() => release(0, 0)}>Toss</button>
      </div>
    </div>
  );
}
